import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import gameAPI from '../services/GameAPI'
import { isSuccessResponse } from '../utils/apiHelpers'

export interface GameStats {
  ships: number
  planets: number
  credits: number
  energy: number
  minerals: number
  research: number
  influence: number
  fleetPower: number
}

export interface Fleet {
  id: string
  name: string
  ships: number
  location: string
  status: 'idle' | 'traveling' | 'combat' | 'exploring'
  destination?: string
  eta?: string
  commander: string
  power: number
}

export interface Planet {
  id: string
  name: string
  type: 'habitable' | 'hostile' | 'resource' | 'empty'
  owner?: string
  buildings: Building[]
  resources: PlanetResource[]
  population: number
  defenseLevel: number
}

export interface Building {
  id: string
  type: 'mine' | 'factory' | 'research_lab' | 'defense' | 'shipyard'
  level: number
  production: number
  energyCost: number
}

export interface PlanetResource {
  type: 'metal' | 'energy' | 'rare_minerals'
  amount: number
  productionRate: number
}

export interface Technology {
  id: string
  name: string
  description: string
  category: 'military' | 'economic' | 'exploration' | 'defense'
  level: number
  cost: number
  duration: number // minutes
  prerequisites: string[]
  status: 'available' | 'researching' | 'completed' | 'locked'
  progress?: number
  timeRemaining?: string
}

interface GameStateContextType {
  // Game Stats
  stats: GameStats
  updateStats: (newStats: Partial<GameStats>) => void
  
  // Fleets
  fleets: Fleet[]
  addFleet: (fleet: Omit<Fleet, 'id'>) => Promise<void>
  updateFleet: (id: string, updates: Partial<Fleet>) => void
  removeFleet: (id: string) => void
  
  // Planets
  planets: Planet[]
  addPlanet: (planet: Omit<Planet, 'id'>) => void
  updatePlanet: (id: string, updates: Partial<Planet>) => void
  
  // Technologies
  technologies: Technology[]
  startResearch: (techId: string) => void
  updateTechProgress: (techId: string, progress: number) => void
  
  // Actions
  buildShip: (type: string, cost: number) => Promise<boolean>
  spendResources: (costs: Partial<GameStats>) => boolean
  canAfford: (costs: Partial<GameStats>) => boolean
  
  // Real-time updates
  isOnline: boolean
  lastUpdate: Date
}

const GameStateContext = createContext<GameStateContextType | undefined>(undefined)

export const useGameState = () => {
  const context = useContext(GameStateContext)
  if (context === undefined) {
    throw new Error('useGameState must be used within a GameStateProvider')
  }
  return context
}

interface GameStateProviderProps {
  children: ReactNode
  userId: string
}

export const GameStateProvider: React.FC<GameStateProviderProps> = ({ children, userId }) => {
  // Initialize game state with loading states
  const [stats, setStats] = useState<GameStats>({
    ships: 0,
    planets: 1,
    credits: 500,
    energy: 100,
    minerals: 100,
    research: 0,
    influence: 10,
    fleetPower: 0
  })

  const [fleets, setFleets] = useState<Fleet[]>([])
  const [planets, setPlanets] = useState<Planet[]>([])
  const [technologies, setTechnologies] = useState<Technology[]>([])
  const [isOnline, setIsOnline] = useState(true)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [isLoading, setIsLoading] = useState(true)

  // Fetch initial data from backend
  useEffect(() => {
    const loadGameData = async () => {
      try {
        setIsLoading(true)
        
        // Fetch user profile and resources
        const profileResult = await gameAPI.getProfile()
        if (isSuccessResponse(profileResult)) {
          setStats(prevStats => ({
            ...prevStats,
            credits: profileResult.data.resources?.crystals || 10000,
            energy: profileResult.data.resources?.metals || 1000,
            minerals: profileResult.data.resources?.energy || 1000,
            // Map backend resources to frontend stats
          }))
        }

        // Fetch fleets
        const fleetsResult = await gameAPI.getFleets()
        if (isSuccessResponse(fleetsResult)) {
          const mappedFleets = fleetsResult.data.map((fleet: any) => ({
            id: fleet.id,
            name: fleet.name,
            ships: Array.isArray(fleet.ships) ? fleet.ships.reduce((total: number, ship: any) => total + (ship.count || 0), 0) : 0,
            location: fleet.location?.sector || 'Unknown',
            status: fleet.status || 'idle',
            commander: 'Admiral Drake', // Default commander
            power: Array.isArray(fleet.ships) ? fleet.ships.reduce((total: number, ship: any) => total + (ship.count || 0) * 10, 0) : 0
          }))
          setFleets(mappedFleets)
          
          // Update fleet power in stats
          const totalFleetPower = mappedFleets.reduce((total, fleet) => total + fleet.power, 0)
          setStats(prev => ({ ...prev, fleetPower: totalFleetPower, ships: mappedFleets.reduce((total, fleet) => total + fleet.ships, 0) }))
        }

        // Initialize default research if no research data
        setTechnologies([
          {
            id: '1',
            name: 'Basic Shipbuilding',
            description: 'Learn to construct basic scout ships',
            category: 'military',
            level: 1,
            cost: 500,
            duration: 30,
            prerequisites: [],
            status: 'available'
          },
          {
            id: '2',
            name: 'Advanced Mining',
            description: 'Increase mineral extraction efficiency',
            category: 'economic',
            level: 1,
            cost: 800,
            duration: 45,
            prerequisites: [],
            status: 'available'
          }
        ])

        // Initialize default planet data
        setPlanets([
          {
            id: '1',
            name: 'Earth',
            type: 'habitable',
            owner: userId,
            buildings: [
              { id: '1', type: 'mine', level: 1, production: 100, energyCost: 50 },
              { id: '2', type: 'factory', level: 1, production: 50, energyCost: 100 }
            ],
            resources: [
              { type: 'metal', amount: 1000, productionRate: 100 },
              { type: 'energy', amount: 1000, productionRate: 200 }
            ],
            population: 1000000,
            defenseLevel: 1
          }
        ])

        setIsOnline(true)
        setLastUpdate(new Date())
      } catch (error) {
        console.error('Failed to load game data:', error)
        setIsOnline(false)
        // Keep using local/default data as fallback
      } finally {
        setIsLoading(false)
      }
    }

    loadGameData()
  }, [userId])

  // Resource generation and sync system
  useEffect(() => {
    const resourceInterval = setInterval(async () => {
      setStats(prevStats => {
        const newStats = { ...prevStats }
        
        // Calculate total resource production from all planets
        const totalProduction = planets.reduce((acc, planet) => {
          if (planet.owner === userId) {
            planet.resources.forEach(resource => {
              if (resource.type === 'metal') {
                acc.minerals += resource.productionRate / (60 * 6) // Per 10 seconds
              } else if (resource.type === 'energy') {
                acc.energy += resource.productionRate / (60 * 6)
              }
            })
          }
          return acc
        }, { minerals: 0, energy: 0, credits: 0 })

        // Base credit generation
        totalProduction.credits = Math.floor(newStats.planets * 10) // 10 credits per planet per 10 seconds

        // Apply production
        newStats.minerals = Math.floor(newStats.minerals + totalProduction.minerals)
        newStats.energy = Math.floor(Math.max(0, newStats.energy + totalProduction.energy - (newStats.ships * 2))) // Ships consume energy
        newStats.credits = Math.floor(newStats.credits + totalProduction.credits)

        // Try to sync resources to backend (every 30 seconds)
        if (Date.now() % 30000 < 10000) {
          try {
            gameAPI.updateProfile({
              resources: {
                crystals: newStats.credits,
                metals: newStats.energy,
                energy: newStats.minerals,
                alloys: 0
              }
            }).catch(error => console.warn('Failed to sync resources to backend:', error))
          } catch (error) {
            console.warn('Resource sync error:', error)
          }
        }

        return newStats
      })
      setLastUpdate(new Date())
    }, 10000) // Update every 10 seconds

    return () => clearInterval(resourceInterval)
  }, [planets, userId])

  // Fleet power calculation
  useEffect(() => {
    const totalFleetPower = fleets.reduce((acc, fleet) => acc + fleet.power, 0)
    const totalShips = fleets.reduce((acc, fleet) => acc + fleet.ships, 0)
    setStats(prev => ({ ...prev, fleetPower: totalFleetPower, ships: totalShips }))
  }, [fleets])

  // Game state management functions
  const updateStats = (newStats: Partial<GameStats>) => {
    setStats(prev => ({ ...prev, ...newStats }))
  }

  const addFleet = async (fleet: Omit<Fleet, 'id'>) => {
    try {
      // Try to save to backend first
      const shipData = {
        name: fleet.name,
        ships: [{ type: 'scout', count: fleet.ships, health: 100 }]
      }
      
      const result = await gameAPI.createFleet(shipData)
      
      let fleetId = Date.now().toString()
      if (isSuccessResponse(result) && result.data.id) {
        fleetId = result.data.id
      } else {
        console.warn('Failed to save fleet to backend, using local ID')
      }

      const newFleet = { ...fleet, id: fleetId }
      setFleets(prev => [...prev, newFleet])
    } catch (error) {
      console.error('Error adding fleet:', error)
      // Fallback to local-only creation
      const newFleet = { ...fleet, id: Date.now().toString() }
      setFleets(prev => [...prev, newFleet])
    }
  }

  const updateFleet = (id: string, updates: Partial<Fleet>) => {
    setFleets(prev => prev.map(fleet => 
      fleet.id === id ? { ...fleet, ...updates } : fleet
    ))
  }

  const removeFleet = (id: string) => {
    setFleets(prev => prev.filter(fleet => fleet.id !== id))
  }

  const addPlanet = (planet: Omit<Planet, 'id'>) => {
    const newPlanet = { ...planet, id: Date.now().toString() }
    setPlanets(prev => [...prev, newPlanet])
    setStats(prev => ({ ...prev, planets: prev.planets + 1 }))
  }

  const updatePlanet = (id: string, updates: Partial<Planet>) => {
    setPlanets(prev => prev.map(planet => 
      planet.id === id ? { ...planet, ...updates } : planet
    ))
  }

  const startResearch = (techId: string) => {
    const tech = technologies.find(t => t.id === techId)
    if (!tech || !canAfford({ credits: tech.cost })) return

    // Spend resources
    spendResources({ credits: tech.cost })
    
    // Start research
    setTechnologies(prev => prev.map(t => 
      t.id === techId 
        ? { ...t, status: 'researching', progress: 0, timeRemaining: `${tech.duration}m` }
        : t
    ))

    // Simulate research completion
    setTimeout(() => {
      setTechnologies(prev => prev.map(t => 
        t.id === techId ? { ...t, status: 'completed', progress: 100 } : t
      ))
    }, tech.duration * 1000) // Convert minutes to milliseconds for demo
  }

  const updateTechProgress = (techId: string, progress: number) => {
    setTechnologies(prev => prev.map(t => 
      t.id === techId ? { ...t, progress } : t
    ))
  }

  const buildShip = async (type: string, cost: number): Promise<boolean> => {
    if (!canAfford({ credits: cost, minerals: cost / 2 })) return false
    
    try {
      // Try to save to backend first
      const targetFleet = fleets.find(f => f.status === 'idle')
      if (targetFleet) {
        const shipData = {
          name: targetFleet.name,
          ships: [{ type, count: 1, health: 100 }]
        }
        
        const result = await gameAPI.createFleet(shipData)
        if (!isSuccessResponse(result)) {
          console.warn('Failed to save fleet to backend, using local update')
        }
      }

      // Update local state
      spendResources({ credits: cost, minerals: Math.floor(cost / 2) })
      
      // Add ship to first available fleet
      if (targetFleet) {
        updateFleet(targetFleet.id, { 
          ships: targetFleet.ships + 1,
          power: targetFleet.power + 100 // Base ship power
        })
      }
      
      return true
    } catch (error) {
      console.error('Error building ship:', error)
      return false
    }
  }

  const spendResources = (costs: Partial<GameStats>): boolean => {
    if (!canAfford(costs)) return false
    
    setStats(prev => ({
      ...prev,
      credits: prev.credits - (costs.credits || 0),
      energy: prev.energy - (costs.energy || 0),
      minerals: prev.minerals - (costs.minerals || 0),
      influence: prev.influence - (costs.influence || 0)
    }))
    
    return true
  }

  const canAfford = (costs: Partial<GameStats>): boolean => {
    return (
      stats.credits >= (costs.credits || 0) &&
      stats.energy >= (costs.energy || 0) &&
      stats.minerals >= (costs.minerals || 0) &&
      stats.influence >= (costs.influence || 0)
    )
  }

  const contextValue: GameStateContextType = {
    stats,
    updateStats,
    fleets,
    addFleet,
    updateFleet,
    removeFleet,
    planets,
    addPlanet,
    updatePlanet,
    technologies,
    startResearch,
    updateTechProgress,
    buildShip,
    spendResources,
    canAfford,
    isOnline,
    lastUpdate
  }

  return (
    <GameStateContext.Provider value={contextValue}>
      {children}
    </GameStateContext.Provider>
  )
}